/**
 * usePromise
 * @description Promise management hook for react
 * @see {@link https://rooks.vercel.app/docs/usePromise}
 */
import {
  type DependencyList,
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useFreshCallback } from 'rooks';

type AsyncFunction<T> = () => Promise<T>;

type PromiseState<T> = {
  data: T | null;
  setData: Dispatch<SetStateAction<T | null>>;
  loading: boolean;
  error: Error | null;
  refresh: () => void;
};

/**
 * This is the usePromise hook from the 'rooks' library but extended with options that allow setting the loading state on refresh
 * @param asyncFunction
 * @param options
 * @param deps
 */
function usePromise<T>(
  asyncFunction: AsyncFunction<T>,
  { setLoadingStateOnRefresh }: { setLoadingStateOnRefresh: boolean } = {
    setLoadingStateOnRefresh: false,
  },
  deps: DependencyList = [],
): PromiseState<T> {
  const [refreshCount, setRefreshCount] = useState(0);
  const refresh = useCallback(() => {
    setRefreshCount((prev) => ++prev);
  }, []);

  const [state, setState] = useState<Omit<PromiseState<T>, 'setData'>>({
    data: null,
    loading: true,
    error: null,
    refresh,
  });

  const freshAsyncCallback = useFreshCallback(asyncFunction);

  useEffect(() => {
    let isMounted = true;

    async function fetchData() {
      try {
        if (setLoadingStateOnRefresh) {
          setState((prevState) => ({
            ...prevState,
            loading: true,
          }));
        }
        const data = await freshAsyncCallback();
        if (isMounted) {
          setState({
            data,
            loading: false,
            error: null,
            refresh,
          });
        }
      } catch (error) {
        if (isMounted) {
          setState({
            data: null,
            loading: false,
            error: error instanceof Error ? error : new Error(String(error)),
            refresh,
          });
        }
      }
    }

    void fetchData();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [freshAsyncCallback, setLoadingStateOnRefresh, refreshCount, ...deps]);

  return useMemo(
    () => ({
      ...state,
      setData: (value) => {
        if (value instanceof Function) {
          setState((prevState) => ({
            ...prevState,
            data: value(prevState.data),
          }));
        } else {
          setState({
            ...state,
            data: value,
          });
        }
      },
    }),
    [state, setState],
  );
}

export { usePromise };
